'use client';
/**
 * Site footer — V26 (20 Sep 2026). Three link columns (For Talent / Company / Connect), then the copyright line
 * with the account links on the right: Login when signed out, Dashboard / Profile / Sign Out when signed in,
 * from real auth state. Contact is a plain link like the others.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';

const footerLinks = [
  {
    label: 'For Talent',
    links: [
      { title: 'Apply as Photographer', href: '/talent/apply' },
      { title: 'Apply as Videographer', href: '/talent/apply' },
      { title: 'Guidelines', href: '/guidelines' },
      { title: 'FAQ', href: '/faq' }
    ]
  },
  {
    label: 'Company',
    links: [
      { title: 'About Us', href: '/about' },
      { title: 'Contact', href: '/contact' },
      { title: 'Privacy Policy', href: '/privacy' },
      { title: 'Terms & Conditions', href: '/terms' }
    ]
  },
  {
    label: 'Connect',
    links: [
      { title: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61573197380793', external: true },
      { title: 'Instagram', href: 'https://www.instagram.com/lovesandphotos/', external: true },
      { title: 'Google', href: 'https://share.google/1BI4qLmistWBRr7a4', external: true },
      { title: 'The Knot', href: 'https://www.theknot.com/marketplace/love-and-photos-springfield-mo-2092546', external: true }
    ]
  }
];

const linkClass = 'block text-sm text-[#777] hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-[#fe395f] focus:ring-offset-2 rounded px-1 -mx-1';

export function dashboardRouteFor(profile) {
  if (profile?.role === 'photographer') return '/talent/dashboard';
  if (profile?.role === 'admin') return '/admin';
  return '/dashboard';
}

export function profileRouteFor(profile, photographerProfile) {
  if (profile?.role === 'photographer') {
    return photographerProfile?.is_videographer ? '/talent/dashboard/videographer/profile' : '/talent/dashboard/profile';
  }
  if (profile?.role === 'admin') return '/lp-console/profile';
  return '/profile';
}

export function Footer() {
  const { user, profile, photographerProfile, signOut } = useAuth();

  const handleSignOut = async () => {
    const result = await signOut();
    if (result && result.success === false) console.error('Sign out failed:', result.error);
  };

  return (
    <footer className='w-full bg-white border-t border-[#eee]' role="contentinfo" aria-label="Site footer">
      <div className='container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-7'>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-7 sm:gap-20 max-w-[900px] pb-8'>
          {footerLinks.map(section => (
            <div key={section.label}>
              <h4 className='text-xs font-semibold uppercase tracking-[0.12em] text-foreground mb-4'>{section.label}</h4>
              <ul className='space-y-[11px]' role="list">
                {section.links.map(link => (
                  <li key={link.title}>
                    {link.external ? (
                      <a href={link.href} className={linkClass} target="_blank" rel="noopener noreferrer" aria-label={`${link.title} - opens in new window`}>
                        {link.title}
                      </a>
                    ) : (
                      <Link to={link.href} className={linkClass}>{link.title}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className='border-t border-[#eee] pt-5 flex flex-wrap items-center justify-between gap-4'>
          <small className='text-sm text-[#777]'>© 2025 Love &amp; Photos. All rights reserved.</small>
          <nav className='flex items-center gap-[18px] text-[13px]' aria-label="Account">
            {user ? (
              <>
                <Link to={dashboardRouteFor(profile)} className='text-[#68636b] hover:text-[#fd5f85] transition-colors'>Dashboard</Link>
                {profile?.role !== 'photographer' && (
                  <Link to={profileRouteFor(profile, photographerProfile)} className='text-[#68636b] hover:text-[#fd5f85] transition-colors'>Profile</Link>
                )}
                <button type="button" onClick={handleSignOut} className='text-[#68636b] hover:text-[#fd5f85] transition-colors'>Sign Out</button>
              </>
            ) : (
              <Link to="/login" className='text-[#68636b] hover:text-[#fd5f85] transition-colors'>Login</Link>
            )}
          </nav>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
